import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { useNavigate } from 'react-router-dom';
import { ReservationsDataOutDTO } from '@api/ringil-api.v2';
import { getCalendarEventTimeData } from '@utils/dateWithTz';
import { CalendarEventCardDetail } from '../CalendarEventCardDetail/CalendarEventCardDetail';
import { twMerge } from 'tailwind-merge';
import { LayoutZone, SurfaceLayout } from '@components/features/calendar-layout/model';
import { BUILT_IN_CARD_LAYOUT } from '@components/features/calendar-layout/defaults';
import { FragmentItemWrapper, FragmentZone } from '@components/features/calendar-layout/fragments/FragmentZone';
import { resolveStateStyle } from '@components/features/calendar-layout/stateStyle';

interface CalendarEventCardProps {
  data: ReservationsDataOutDTO;
  link: string;
  /**
   * Zone-based layout; falls back to the built-in one. Callers that hold a legacy flat fragment
   * list (the company `CALENDAR_CARD_FRAGMENTS` payload) adapt it with `layoutFromLegacyFragments`.
   */
  layout?: SurfaceLayout;
  /** Layout for the hover popover this card opens; falls back to the built-in detail layout. */
  detailLayout?: SurfaceLayout;
  onStateChange: () => void;
  /** Designer-only hooks: wrap each zone container / each rendered fragment with edit affordances. */
  zoneWrapper?: (zone: LayoutZone, node: React.ReactNode) => React.ReactNode;
  itemWrapper?: FragmentItemWrapper;
  /** Designer-only: suppress the hover popover so it does not cover the card being edited. */
  disableDetail?: boolean;
  /** Designer-only: let the card grow to its content instead of filling (and clipping to) its slot. */
  unconstrained?: boolean;
  /**
   * Result of the calendar's client-side highlight filters: `match` outlines the card, `dim` greys it
   * out, `null`/omitted leaves it untouched. Nothing is hidden — a dimmed card still occupies its slot.
   */
  highlight?: 'match' | 'dim' | null;
}

export const CalendarEventCard = ({
  data,
  link,
  layout,
  detailLayout,
  onStateChange,
  highlight,
  zoneWrapper,
  itemWrapper,
  disableDetail,
  unconstrained,
}: CalendarEventCardProps) => {
  const detailRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Popover placement: a horizontal utility class (which side of the card it sits on) plus a pixel
  // top offset relative to the card's positioning container, clamped to keep the popover fully
  // inside the visible scroll viewport.
  const [detailPos, setDetailPos] = useState<{ horizontal: string; topPx: number | null }>({ horizontal: 'pl-4 left-full', topPx: null });
  const navigate = useNavigate();
  const { reservationId, timeslot, state } = data;
  const [detailVisible, setDetailVisible] = useState(false);

  const style = resolveStateStyle(state);
  const timeData = getCalendarEventTimeData(timeslot);

  const cardLayout = useMemo<SurfaceLayout>(() => layout ?? BUILT_IN_CARD_LAYOUT, [layout]);

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseOver = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setDetailVisible(true);
  };

  const handleMouseOut = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setDetailVisible(false);
    }, 50);
  };

  const checkOverflow = () => {
    const hoverCardEl = detailRef.current;
    const cardEl = cardRef.current;
    const parentEl = document.querySelector('#calendar .fc');

    if (!hoverCardEl || !cardEl || !parentEl) return;

    const hoverCardElRect = hoverCardEl.getBoundingClientRect();
    const parentElRect = parentEl.getBoundingClientRect();
    const cardRect = cardEl.getBoundingClientRect();

    // Sticky column header height. calendar-multi grows it to min-h-16 (64px) and beyond for the
    // timezone row, so measure it rather than assuming the base 36px (h-9) header. It hides the top
    // slice of the viewport, so the popover must stay below it.
    const headerEl = parentEl.querySelector('.fc-col-header');
    const headerHeight = headerEl ? headerEl.getBoundingClientRect().height : 36;

    // Everything is measured against the visible scroll viewport (#calendar .fc is the scroll
    // container), not the full day's content. The visible band runs from just below the sticky
    // header to the bottom edge.
    const popoverHeight = hoverCardElRect.height;
    const viewportTop = parentElRect.top + headerHeight;
    const viewportBottom = parentElRect.bottom;

    const overflowRight = cardRect.right + hoverCardElRect.width > parentElRect.right;
    const overflowLeft = cardRect.left - hoverCardElRect.width < parentElRect.left;

    let horizontal: string;
    let anchorTop: number;

    if (!overflowRight || !overflowLeft) {
      // Fits beside the card — prefer its right, flip to the left otherwise. Rest 40px above the
      // card top (the former -top-10 look) and clamp so the popover stays wholly on screen even when
      // the hovered event is itself only partially scrolled into view.
      horizontal = overflowRight ? 'pr-4 right-full' : 'pl-4 left-full';
      const maxTop = Math.max(viewportTop, viewportBottom - popoverHeight);
      anchorTop = Math.min(Math.max(cardRect.top - 40, viewportTop), maxTop);
    } else {
      // Neither side fits (single / full-width column): stack the popover above the card, or below
      // when there is no room above, so it never covers the card's own details.
      const gap = 8;
      horizontal = 'left-0';
      const aboveTop = cardRect.top - popoverHeight - gap;
      const belowTop = cardRect.bottom + gap;
      if (aboveTop >= viewportTop) {
        anchorTop = aboveTop;
      } else if (belowTop + popoverHeight <= viewportBottom) {
        anchorTop = belowTop;
      } else {
        // Popover taller than the room on either side — pin to whichever side has more space.
        anchorTop = cardRect.top - viewportTop >= viewportBottom - cardRect.bottom ? viewportTop : Math.max(viewportTop, viewportBottom - popoverHeight);
      }
    }

    // Express the resolved viewport position relative to the card's positioning container.
    const topPx = anchorTop - cardRect.top;

    setDetailPos({ horizontal, topPx });
  };

  // useLayoutEffect so the measured position is applied before paint (no first-frame flash).
  useLayoutEffect(() => {
    if (detailVisible) checkOverflow();
  }, [detailVisible]);

  const handleStateChange = () => {
    if (onStateChange) {
      onStateChange();
    }
    setDetailVisible(false);
  };

  const zoneProps = { surface: 'card' as const, layout: cardLayout, data, stateStyle: style, itemWrapper };

  /** Zones render bare in the app; the designer wraps each one to make it a drop target. */
  const zone = (which: LayoutZone) => {
    const node = <FragmentZone {...zoneProps} zone={which} />;
    return zoneWrapper ? zoneWrapper(which, node) : node;
  };

  return (
    <div
      ref={cardRef}
      className={clsx('relative z-0 w-full group hover:z-50', unconstrained ? 'h-auto' : 'h-full')}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      <div
        className={clsx(
          style && style.wrapper,
          'custom-fullcalendar-event bg-base-base border border-gray-200 border-solid w-full left-0 z-0 top-0 p-2 pl-3.5 rounded-lg text-black cursor-pointer',
          // In the calendar the card is absolutely positioned and fills its slot; the designer needs
          // it to grow to its content instead, so nothing being edited is hidden.
          unconstrained ? 'relative h-auto' : 'absolute h-full',
          // Every card lifts on hover — with or without highlighting — so the one under the cursor
          // reads as the active one (the card is absolutely positioned, hence the shadow rather than
          // a transform, which would fight FullCalendar's layout).
          'transition-[opacity,filter,box-shadow] duration-150 group-hover:shadow-[0_4px_12px_0_rgba(0,0,0,0.15)]',
          timeData?.durationMinutes < 30 && 'pt-1 pb-0',
          // Matched cards are outlined rather than re-bordered, so the card's own state border stays;
          // the outline thickens on hover, matching the lift the other states get.
          highlight === 'match' && 'outline outline-2 -outline-offset-2 outline-content-brand group-hover:outline-[3px]',
          // Dimming sits on the card only — the hover detail popover is a sibling and stays readable.
          // Hovering restores the card to full strength so a greyed-out reservation can still be read.
          highlight === 'dim' && 'opacity-40 grayscale group-hover:opacity-100 group-hover:grayscale-0',
        )}
        data-event-id={reservationId}
        // An empty link means "not navigable" — the designer renders the card as a preview.
        onClick={() => link && navigate(link)}
      >
        {/* `[&>*]:shrink-0`: on a short event the card is shorter than its content, and flex children
            would otherwise be squeezed vertically. Fragments that clip their own overflow (the title
            row's `truncate`) then lose their text completely, so rows keep their natural height and
            the surplus is simply cut off at the card's bottom edge. */}
        <div className={clsx('flex flex-col [&>*]:shrink-0', !unconstrained && 'overflow-hidden h-full')}>
          <div className="flex justify-between text-sm gap-x-2 mb-1 w-full min-w-0">
            <div className="font-bold text-content-primary min-w-0 inline-flex leading-none items-center">{zone(LayoutZone.HeaderLeft)}</div>
            {zone(LayoutZone.HeaderRight)}
          </div>

          {/* The body owns the vertical rhythm between its fragments. Keeping it here rather than as a
              bottom margin on each fragment means a fragment carries no spacing into the header row,
              where that margin would push its text off the row's centre line. */}
          <div className="flex flex-col gap-y-1.5 [&>*]:shrink-0">{zone(LayoutZone.Body)}</div>

          <div className={clsx(style && style.line, 'w-1.5 h-full absolute left-0 top-0 rounded-l-lg')} />
        </div>
      </div>

      {/* Mount the popover only while it is visible: keeping every event's detail (and its
          on-mount possible-transitions fetch) rendered up front would cost N hidden subtrees and a
          burst of API calls on calendar load. useLayoutEffect measures it right after this mount. */}
      {detailVisible && !disableDetail && (
        <CalendarEventCardDetail
          ref={detailRef}
          data={data}
          style={style}
          link={link}
          layout={detailLayout}
          className={twMerge(clsx('z-50 block', detailPos.horizontal))}
          positionStyle={detailPos.topPx != null ? { top: `${detailPos.topPx}px` } : undefined}
          onStateChange={handleStateChange}
        />
      )}
    </div>
  );
};
